import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError, formatDate, formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import {
  Clock,
  Plus,
  Trash2,
  Send,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Pencil,
  Calendar,
  Inbox,
  User as UserIcon,
  Download,
  FileText,
  FileSpreadsheet,
} from "lucide-react";

const STATUS_META = {
  draft:     { label: "Draft",     color: "bg-[#F1F5F9] text-[#334155]" },
  submitted: { label: "Submitted", color: "bg-[#DBEAFE] text-[#1E40AF]" },
  approved:  { label: "Approved",  color: "bg-[#E8ECE7] text-[#166534]" },
  rejected:  { label: "Rejected",  color: "bg-[#FEE2E2] text-[#991B1B]" },
};

// Parse a YYYY-MM-DD string into a *local* Date at midnight so weekday math
// doesn't slip across timezones (`new Date("2026-05-25")` parses as UTC,
// which becomes Sunday in EST and silently breaks weekday lookups).
function parseLocalDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

// Return YYYY-MM-DD for the SUNDAY of the calendar week containing `iso`
// (US payroll convention: Sunday → Saturday).
function sundayOf(iso) {
  const date = typeof iso === "string" ? parseLocalDate(iso) : new Date(iso);
  // JS getDay: Sunday=0, Saturday=6 — already what we want.
  date.setDate(date.getDate() - date.getDay());
  return toIsoDate(date);
}

function weekDates(weekStart) {
  const start = parseLocalDate(weekStart);
  return Array.from({ length: 7 }, (_, i) => {
    const dt = new Date(start);
    dt.setDate(start.getDate() + i);
    return toIsoDate(dt);
  });
}

function weekLabel(weekStart) {
  if (!weekStart) return "";
  const days = weekDates(weekStart);
  const start = parseLocalDate(days[0]);
  const end = parseLocalDate(days[6]);
  const fmt = { month: "short", day: "numeric" };
  return `${start.toLocaleDateString(undefined, fmt)} – ${end.toLocaleDateString(undefined, fmt)}, ${start.getFullYear()}`;
}

// --- US Federal holidays (observed-date list, recomputed per year as needed)
// Returns a map { "YYYY-MM-DD": "Holiday name" }
function usFederalHolidays(years) {
  const set = new Set(Array.isArray(years) ? years : [years]);
  const out = {};
  const nthWeekdayOf = (year, month, weekday, n) => {
    // weekday: 0=Sun..6=Sat. month: 1-12. n: 1-5
    const first = new Date(year, month - 1, 1);
    const offset = (weekday - first.getDay() + 7) % 7;
    const day = 1 + offset + (n - 1) * 7;
    return toIsoDate(new Date(year, month - 1, day));
  };
  const lastWeekdayOf = (year, month, weekday) => {
    const last = new Date(year, month, 0);
    const offset = (last.getDay() - weekday + 7) % 7;
    return toIsoDate(new Date(year, month - 1, last.getDate() - offset));
  };
  for (const y of set) {
    // Fixed-date holidays — moved to nearest weekday when they fall on Sat/Sun.
    const observed = (iso, name) => {
      const d = parseLocalDate(iso);
      let obs = d;
      if (d.getDay() === 0) obs = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
      if (d.getDay() === 6) obs = new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1);
      out[toIsoDate(obs)] = name;
    };
    observed(`${y}-01-01`, "New Year's Day");
    out[nthWeekdayOf(y, 1, 1, 3)] = "Martin Luther King Jr. Day";
    out[nthWeekdayOf(y, 2, 1, 3)] = "Presidents' Day";
    out[lastWeekdayOf(y, 5, 1)] = "Memorial Day";
    observed(`${y}-06-19`, "Juneteenth");
    observed(`${y}-07-04`, "Independence Day");
    out[nthWeekdayOf(y, 9, 1, 1)] = "Labor Day";
    out[nthWeekdayOf(y, 10, 1, 2)] = "Columbus Day";
    observed(`${y}-11-11`, "Veterans Day");
    out[nthWeekdayOf(y, 11, 4, 4)] = "Thanksgiving Day";
    observed(`${y}-12-25`, "Christmas Day");
  }
  return out;
}

export default function Timesheets() {
  const { user } = useAuth();
  const [tab, setTab] = useState("mine"); // 'mine' | 'review'
  const [mine, setMine] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const isAdmin = user?.role === "admin" || user?.role === "executive_director";

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [mineRes, pendingRes] = await Promise.all([
        api.get("/timesheets", { params: { user_id: user.id } }),
        api.get("/timesheets/pending").catch(() => ({ data: [] })),
      ]);
      setMine(mineRes.data || []);
      setPending(pendingRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const canViewReports = isAdmin || !!user?.has_direct_reports;
  const tabs = [
    { key: "mine",   label: "My timesheets", count: mine.length, Icon: UserIcon },
    { key: "review", label: "To review",      count: pending.length, Icon: Inbox, hidden: pending.length === 0 && !isAdmin },
    { key: "reports", label: "Reports",       count: null, Icon: Calendar, hidden: !canViewReports },
  ];

  return (
    <div className="space-y-6" data-testid="timesheets-page">
      {/* Hero */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Operations · Hours</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Timesheets</h1>
          <p className="text-[#334155] mt-2 max-w-2xl">
            Log your hours per week with task notes. Submit to your supervisor
            for review and approval.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
          data-testid="new-timesheet-btn"
        >
          <Plus className="w-4 h-4" /> New timesheet
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-2 inline-flex gap-1 flex-wrap">
        {tabs.filter((t) => !t.hidden).map(({ key, label, count, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`ts-tab-${key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              tab === key ? "bg-[#1E3A8A] text-white" : "text-[#1E3A8A] hover:bg-[#F9F8F6]"
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            <span className={`text-xs px-2 py-0.5 rounded-full ${tab === key ? "bg-white/20 text-white" : "bg-[#F1F5F9] text-[#334155]"}`}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Body */}
      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : tab === "mine" ? (
        <TimesheetList
          items={mine}
          isReview={false}
          onEdit={(ts) => setEditing(ts)}
          onView={(ts) => setEditing(ts)}
          onRefresh={refresh}
        />
      ) : tab === "review" ? (
        <TimesheetList
          items={pending}
          isReview
          onEdit={(ts) => setReviewing(ts)}
          onView={(ts) => setReviewing(ts)}
          onRefresh={refresh}
        />
      ) : (
        <TimesheetReportsCard isAdmin={isAdmin} />
      )}

      {tab === "reports" && !canViewReports && (
        <div className="text-center py-12 text-[#1E3A8A]">Reports are available to admin/ED and supervisors.</div>
      )}

      {showCreate && (
        <TimesheetEditor
          mode="create"
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); refresh(); }}
        />
      )}
      {editing && (
        <TimesheetEditor
          mode="edit"
          ts={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {reviewing && (
        <TimesheetEditor
          mode="review"
          ts={reviewing}
          onClose={() => setReviewing(null)}
          onSaved={() => { setReviewing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function TimesheetList({ items, isReview, onEdit, onRefresh }) {
  if (items.length === 0) {
    return (
      <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center" data-testid="ts-empty">
        <Clock className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
        <h3 className="font-display text-lg font-semibold text-[#0F172A]">
          {isReview ? "No pending timesheets" : "No timesheets yet"}
        </h3>
        <p className="text-sm text-[#334155] mt-2">
          {isReview
            ? "When a team member submits a timesheet, it'll show up here."
            : 'Click "New timesheet" to log this week\'s hours.'}
        </p>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl overflow-hidden">
      <table className="w-full text-sm" data-testid="ts-table">
        <thead className="bg-[#F9F8F6] border-b border-[#E5E5E0]">
          <tr>
            {isReview && (
              <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Owner</th>
            )}
            <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Week</th>
            <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Status</th>
            <th className="text-right px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider">Hours</th>
            <th className="text-left px-4 py-3 font-medium text-[#1E3A8A] uppercase text-[10px] tracking-wider hidden md:table-cell">Submitted</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((ts) => {
            const meta = STATUS_META[ts.status] || STATUS_META.draft;
            return (
              <tr key={ts.id} className="border-t border-[#F0EFEB]" data-testid={`ts-row-${ts.id}`}>
                {isReview && (
                  <td className="px-4 py-3 text-sm text-[#0F172A] font-medium">{ts.user_name}</td>
                )}
                <td className="px-4 py-3 text-sm text-[#0F172A]">
                  {weekLabel(ts.week_start_date)}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider ${meta.color}`}>
                    {meta.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-medium text-[#0F172A]">{ts.total_hours ?? 0}</td>
                <td className="px-4 py-3 text-xs text-[#334155] hidden md:table-cell">
                  {ts.submitted_at ? formatDateTime(ts.submitted_at) : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onEdit(ts)}
                    data-testid={`ts-open-${ts.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E3A8A] text-white text-xs font-medium hover:bg-[#172554]"
                  >
                    {isReview ? "Review" : (ts.status === "draft" || ts.status === "rejected" ? "Edit" : "Open")}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function TimesheetEditor({ mode, ts, onClose, onSaved }) {
  const { user } = useAuth();
  const isCreate = mode === "create";
  const isReview = mode === "review";
  const editable = !isReview && (ts ? (ts.status === "draft" || ts.status === "rejected") : true);
  const [weekStart, setWeekStart] = useState(ts?.week_start_date || sundayOf(toIsoDate(new Date())));
  const [entries, setEntries] = useState(ts?.entries?.length ? ts.entries : []);
  const [notes, setNotes] = useState(ts?.notes || "");
  const [careSeekers, setCareSeekers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState("");

  useEffect(() => {
    api.get("/care-seekers", { params: { status: "active" } })
      .then(({ data }) => setCareSeekers(data || []))
      .catch(() => setCareSeekers([]));
  }, []);

  const total = useMemo(
    () => entries.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0),
    [entries],
  );

  const incompleteCount = useMemo(
    () => entries.filter((e) => {
      const hrs = parseFloat(e.hours) || 0;
      const desc = (e.task_description || "").trim();
      const rowHasAnything = hrs > 0 || desc;
      return rowHasAnything && (hrs <= 0 || !desc);
    }).length,
    [entries],
  );

  const days = weekDates(weekStart);
  // Build the holiday lookup for any year the current week touches —
  // weeks that cross New Year would otherwise miss the holiday on the
  // far side of the boundary.
  const holidayMap = useMemo(() => {
    if (!days.length) return {};
    const years = new Set(days.map((d) => Number(d.slice(0, 4))));
    return usFederalHolidays(Array.from(years));
  }, [days]);
  const holidaysThisWeek = days
    .map((d) => ({ date: d, name: holidayMap[d] }))
    .filter((h) => h.name);
  const setEntry = (i, patch) => setEntries(entries.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const addEntry = () =>
    setEntries([...entries, { date: days[0], hours: 0, care_seeker_id: "", task_description: "" }]);
  const removeEntry = (i) => setEntries(entries.filter((_, idx) => idx !== i));

  const save = async (then) => {
    // Drop rows that are completely empty (no hours AND no description).
    // For everything else, both hours and a task description are required —
    // we refuse to silently drop data the user typed.
    const meaningful = entries.filter(
      (e) => (parseFloat(e.hours) || 0) > 0 || (e.task_description || "").trim(),
    );
    const incomplete = meaningful
      .map((e, idx) => ({ e, idx }))
      .filter(({ e }) => {
        const hrs = parseFloat(e.hours) || 0;
        const desc = (e.task_description || "").trim();
        return !desc || hrs <= 0;
      });
    if (incomplete.length > 0) {
      const first = incomplete[0].e;
      const dayLabel = first.date
        ? parseLocalDate(first.date).toLocaleDateString(undefined, {
            weekday: "short", month: "short", day: "numeric",
          })
        : `Row ${incomplete[0].idx + 1}`;
      const missing = !(first.task_description || "").trim() ? "a task description" : "hours";
      toast.error(
        `${dayLabel} is missing ${missing}. Fix or remove it before ${then === "submit" ? "submitting" : "saving"}.`,
      );
      return;
    }
    if (then === "submit" && meaningful.length === 0) {
      toast.error("Add at least one entry before submitting.");
      return;
    }
    setSaving(true);
    try {
      const cleanedEntries = meaningful.map((e) => ({
        date: e.date,
        hours: parseFloat(e.hours) || 0,
        care_seeker_id: e.care_seeker_id || null,
        task_description: e.task_description.trim(),
      }));
      let saved = ts;
      if (isCreate) {
        const { data } = await api.post("/timesheets", {
          week_start_date: weekStart,
          entries: cleanedEntries,
          notes: notes.trim() || null,
        });
        saved = data;
      } else {
        const { data } = await api.patch(`/timesheets/${ts.id}`, {
          entries: cleanedEntries,
          notes: notes.trim() || null,
        });
        saved = data;
      }
      if (then === "submit") {
        await api.post(`/timesheets/${saved.id}/submit`);
        toast.success("Submitted for review");
      } else {
        toast.success("Saved");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const review = async (action) => {
    if (action === "reject" && !reviewerNotes.trim()) {
      toast.error("Please add a note explaining what to fix.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/timesheets/${ts.id}/${action}`, { reviewer_notes: reviewerNotes.trim() || null });
      toast.success(action === "approve" ? "Approved" : "Returned to staff");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const reopen = async () => {
    setSaving(true);
    try {
      await api.post(`/timesheets/${ts.id}/reopen`);
      toast.success("Re-opened — owner can edit & resubmit");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const csName = (id) => {
    const c = careSeekers.find((x) => x.id === id);
    if (!c) return "—";
    return `${c.first_name} ${c.last_name}${c.display_id ? ` (${c.display_id})` : ""}`;
  };

  const isAdmin = user?.role === "admin" || user?.role === "executive_director";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="ts-dialog">
      <div className="bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#E5E5E0] flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-semibold text-[#0F172A]">
              {isCreate ? "New timesheet" : (isReview ? `Review · ${ts.user_name}` : "Timesheet")}
            </h2>
            <p className="text-xs text-[#334155] mt-1">{weekLabel(weekStart)}</p>
            {ts && (
              <div className="mt-2 inline-flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${STATUS_META[ts.status]?.color}`}>
                  {STATUS_META[ts.status]?.label}
                </span>
                {ts.reviewed_by_name && (
                  <span className="text-xs text-[#334155]">
                    by {ts.reviewed_by_name} · {formatDate(ts.reviewed_at)}
                  </span>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F9F8F6] rounded-full" data-testid="ts-dialog-close">
            <XCircle className="w-5 h-5 text-[#334155]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isCreate && (
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Week starting (Sunday)</label>
              <input
                type="date"
                value={weekStart}
                onChange={(e) => {
                  if (!e.target.value) return;
                  const snapped = sundayOf(e.target.value);
                  setWeekStart(snapped);
                  if (snapped !== e.target.value) toast.message("Snapped to the Sunday of that week");
                }}
                className="px-4 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm"
                data-testid="ts-week-input"
              />
              <p className="text-xs text-[#1E3A8A]/70 mt-1.5">
                Full calendar week, Sunday through Saturday. US federal holidays are flagged below.
              </p>
            </div>
          )}

          {ts?.status === "rejected" && ts?.reviewer_notes && (
            <div className="bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#991B1B] mb-1">
                Reviewer asked for changes
              </div>
              <div className="text-sm text-[#0F172A]">{ts.reviewer_notes}</div>
            </div>
          )}
          {ts?.status === "approved" && ts?.reviewer_notes && (
            <div className="bg-[#E8ECE7] border border-[#86EFAC] rounded-xl p-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#166534] mb-1">
                Reviewer notes
              </div>
              <div className="text-sm text-[#0F172A]">{ts.reviewer_notes}</div>
            </div>
          )}

          {editable && holidaysThisWeek.length > 0 && (
            <div
              className="bg-[#E8ECE7] border border-[#7E937D]/40 rounded-xl p-3 text-sm text-[#166534] flex items-start gap-2"
              data-testid="ts-holiday-banner"
            >
              <span className="font-semibold">US holidays this week:</span>
              <span>
                {holidaysThisWeek
                  .map((h) =>
                    `${parseLocalDate(h.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${h.name}`,
                  )
                  .join(" · ")}
              </span>
            </div>
          )}

          {editable && incompleteCount > 0 && (
            <div
              className="bg-[#FEF3C7] border border-[#FCD34D] rounded-xl p-3 text-sm text-[#854D0E] flex items-start gap-2"
              data-testid="ts-incomplete-banner"
            >
              <span className="font-semibold">Heads up:</span>
              <span>
                {incompleteCount} {incompleteCount === 1 ? "entry needs" : "entries need"} a task description and hours &gt; 0 before you can submit. Incomplete rows are outlined in red below.
              </span>
            </div>
          )}

          {/* Entries */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-[#E5E5E0] rounded-xl overflow-hidden" data-testid="ts-entries-table">
              <thead className="bg-[#F9F8F6]">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A] w-44">Date</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A] w-24">Hours</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A] w-64">Care Seeker (optional)</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#1E3A8A]">Task description</th>
                  {editable && <th className="px-3 py-2 w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 ? (
                  <tr>
                    <td colSpan={editable ? 5 : 4} className="px-3 py-6 text-center text-[#334155] text-sm">
                      No entries yet — {editable ? "click Add entry to start." : "no hours logged."}
                    </td>
                  </tr>
                ) : (
                  entries.map((e, i) => {
                    const hrs = parseFloat(e.hours) || 0;
                    const desc = (e.task_description || "").trim();
                    const rowHasAnything = hrs > 0 || desc;
                    const missingHours = rowHasAnything && hrs <= 0;
                    const missingDesc = rowHasAnything && !desc;
                    return (
                    <tr key={i} className="border-t border-[#F0EFEB]" data-testid={`ts-entry-${i}`}>
                      <td className="px-3 py-2">
                        {editable ? (
                          <select
                            value={e.date}
                            onChange={(ev) => setEntry(i, { date: ev.target.value })}
                            className="w-full px-2 py-1.5 rounded-lg bg-[#F9F8F6] border border-[#E5E5E0] text-sm"
                          >
                            {days.map((d) => {
                              const label = parseLocalDate(d).toLocaleDateString(undefined, {
                                weekday: "short", month: "short", day: "numeric",
                              });
                              const h = holidayMap[d];
                              return (
                                <option key={d} value={d}>
                                  {label}{h ? ` · ${h}` : ""}
                                </option>
                              );
                            })}
                          </select>
                        ) : (
                          <span>
                            {formatDate(e.date)}
                            {holidayMap[e.date] && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wider text-[#92400E] bg-[#FEF3C7] px-1.5 py-0.5 rounded-full">
                                {holidayMap[e.date]}
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editable ? (
                          <input
                            type="number"
                            min="0"
                            max="24"
                            step="0.25"
                            value={e.hours}
                            onChange={(ev) => setEntry(i, { hours: ev.target.value })}
                            className={`w-full px-2 py-1.5 rounded-lg bg-[#F9F8F6] border text-sm ${
                              missingHours ? "border-[#DC2626] ring-1 ring-[#FCA5A5]" : "border-[#E5E5E0]"
                            }`}
                          />
                        ) : (
                          <span className="font-medium">{e.hours}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editable ? (
                          <select
                            value={e.care_seeker_id || ""}
                            onChange={(ev) => setEntry(i, { care_seeker_id: ev.target.value })}
                            className="w-full px-2 py-1.5 rounded-lg bg-[#F9F8F6] border border-[#E5E5E0] text-sm"
                          >
                            <option value="">— None —</option>
                            {careSeekers.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.first_name} {c.last_name}{c.display_id ? ` (${c.display_id})` : ""}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-[#334155]">{csName(e.care_seeker_id)}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {editable ? (
                          <textarea
                            rows={2}
                            value={e.task_description}
                            onChange={(ev) => setEntry(i, { task_description: ev.target.value })}
                            onKeyDown={(ev) => {
                              // Auto-bullet on Enter — insert "• " on the new line
                              // so each line becomes a bullet point.
                              if (ev.key === "Enter" && !ev.shiftKey) {
                                ev.preventDefault();
                                const ta = ev.target;
                                const cursor = ta.selectionStart;
                                const before = ta.value.slice(0, cursor);
                                const after = ta.value.slice(cursor);
                                const insert = "\n• ";
                                const next = before + insert + after;
                                setEntry(i, { task_description: next });
                                requestAnimationFrame(() => {
                                  ta.selectionStart = ta.selectionEnd = cursor + insert.length;
                                });
                              }
                            }}
                            onFocus={(ev) => {
                              // Auto-prepend "• " to the very first line if the
                              // user starts typing into an empty field.
                              if (!ev.target.value) {
                                setEntry(i, { task_description: "• " });
                              }
                            }}
                            placeholder="What did you work on? (Press Enter for a new bullet)"
                            data-testid={`ts-desc-${i}`}
                            className={`w-full px-2 py-1.5 rounded-lg bg-[#F9F8F6] border text-sm resize-y min-h-[40px] ${
                              missingDesc ? "border-[#DC2626] ring-1 ring-[#FCA5A5]" : "border-[#E5E5E0]"
                            }`}
                          />
                        ) : (
                          <BulletList text={e.task_description} />
                        )}
                      </td>
                      {editable && (
                        <td className="px-3 py-2">
                          <button
                            onClick={() => removeEntry(i)}
                            className="p-1 text-[#991B1B] hover:bg-[#FEE2E2] rounded"
                            data-testid={`ts-remove-entry-${i}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      )}
                    </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot className="bg-[#F9F8F6] border-t border-[#E5E5E0]">
                <tr>
                  <td className="px-3 py-2 text-xs uppercase tracking-wider text-[#1E3A8A]">Total</td>
                  <td className="px-3 py-2 font-semibold text-[#0F172A]" data-testid="ts-total-hours">{total.toFixed(2)}</td>
                  <td colSpan={editable ? 3 : 2}></td>
                </tr>
              </tfoot>
            </table>
            {editable && (
              <button
                onClick={addEntry}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-sm font-medium text-[#1E3A8A] hover:bg-[#F9F8F6]"
                data-testid="ts-add-entry"
              >
                <Plus className="w-4 h-4" /> Add entry
              </button>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Notes for the week (optional)</label>
            {editable ? (
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm"
                placeholder="Anything your reviewer should know about this week"
                data-testid="ts-notes"
              />
            ) : (
              <div className="text-sm text-[#0F172A] whitespace-pre-wrap">{notes || <span className="text-[#0F172A]/40">No notes</span>}</div>
            )}
          </div>

          {isReview && (
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Reviewer note (required to reject)</label>
              <textarea
                value={reviewerNotes}
                onChange={(e) => setReviewerNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-xl bg-[#F9F8F6] border border-[#E5E5E0] text-sm"
                data-testid="ts-reviewer-notes"
                placeholder="What needs to change? Or leave blank and approve."
              />
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-[#E5E5E0] flex flex-wrap items-center justify-between gap-3 bg-[#F9F8F6]">
          <div className="text-xs text-[#334155]">
            <Calendar className="inline w-3.5 h-3.5 mr-1" /> {weekLabel(weekStart)}
          </div>
          <div className="flex gap-2 flex-wrap">
            {isReview ? (
              <>
                <button
                  onClick={() => review("reject")}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#FCA5A5] text-[#991B1B] text-sm font-medium hover:bg-[#FEE2E2] disabled:opacity-50"
                  data-testid="ts-reject-btn"
                >
                  <XCircle className="w-4 h-4" /> Reject
                </button>
                <button
                  onClick={() => review("approve")}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#166534] text-white text-sm font-medium hover:bg-[#14532D] disabled:opacity-50"
                  data-testid="ts-approve-btn"
                >
                  <CheckCircle2 className="w-4 h-4" /> Approve
                </button>
              </>
            ) : (
              <>
                {ts?.status === "approved" && isAdmin && (
                  <button
                    onClick={reopen}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-sm font-medium hover:bg-white"
                    data-testid="ts-reopen-btn"
                  >
                    <RotateCcw className="w-4 h-4" /> Re-open
                  </button>
                )}
                {editable && (
                  <>
                    <button
                      onClick={() => save("save")}
                      disabled={saving}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] text-sm font-medium disabled:opacity-50"
                      data-testid="ts-save-btn"
                    >
                      <Pencil className="w-4 h-4" /> Save draft
                    </button>
                    <button
                      onClick={() => save("submit")}
                      disabled={saving || entries.length === 0 || incompleteCount > 0}
                      className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-50"
                      data-testid="ts-submit-btn"
                    >
                      <Send className="w-4 h-4" /> {saving ? "Working…" : "Submit for review"}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function TimesheetReportsCard({ isAdmin }) {
  const [employees, setEmployees] = useState([]);
  const [userId, setUserId] = useState("");
  const [preset, setPreset] = useState("this_week");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const path = isAdmin ? "/users" : "/users/direct-reports";
    api.get(path).then(({ data }) => {
      const staff = (data || []).filter((u) => u.active !== false && u.role !== "volunteer");
      setEmployees(staff);
    }).catch(() => {});
  }, [isAdmin]);

  // Preset → date window. Sunday → Saturday weeks (US payroll convention).
  useEffect(() => {
    if (preset === "custom") return;
    const today = new Date();
    const startOfThisSunday = (d) => {
      const x = new Date(d);
      x.setDate(x.getDate() - x.getDay());
      return x;
    };
    if (preset === "this_week") {
      const s = startOfThisSunday(today);
      const e = new Date(s); e.setDate(s.getDate() + 6);
      setFrom(toIsoDate(s)); setTo(toIsoDate(e));
    } else if (preset === "last_week") {
      const s = startOfThisSunday(today); s.setDate(s.getDate() - 7);
      const e = new Date(s); e.setDate(s.getDate() + 6);
      setFrom(toIsoDate(s)); setTo(toIsoDate(e));
    } else if (preset === "biweekly") {
      const s = startOfThisSunday(today); s.setDate(s.getDate() - 7);
      const e = new Date(s); e.setDate(s.getDate() + 13);
      setFrom(toIsoDate(s)); setTo(toIsoDate(e));
    } else if (preset === "this_month") {
      const f = new Date(today.getFullYear(), today.getMonth(), 1);
      const t = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setFrom(toIsoDate(f)); setTo(toIsoDate(t));
    } else if (preset === "last_month") {
      const f = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const t = new Date(today.getFullYear(), today.getMonth(), 0);
      setFrom(toIsoDate(f)); setTo(toIsoDate(t));
    } else if (preset === "this_year") {
      const f = new Date(today.getFullYear(), 0, 1);
      const t = new Date(today.getFullYear(), 11, 31);
      setFrom(toIsoDate(f)); setTo(toIsoDate(t));
    } else if (preset === "last_year") {
      const f = new Date(today.getFullYear() - 1, 0, 1);
      const t = new Date(today.getFullYear() - 1, 11, 31);
      setFrom(toIsoDate(f)); setTo(toIsoDate(t));
    }
  }, [preset]);

  const download = async (format) => {
    if (!userId) { toast.error("Pick an employee"); return; }
    if (!from || !to) { toast.error("Choose a date range"); return; }
    const employee = employees.find((e) => e.id === userId);
    const stamp = `${employee?.name?.replace(/\s+/g, "_") || "employee"}_${from}_to_${to}`.toLowerCase();
    setBusy(true);
    try {
      const path = `/reports/timesheets.${format}?user_id=${encodeURIComponent(userId)}&date_from=${from}&date_to=${to}`;
      const res = await api.get(path, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: format === "csv" ? "text/csv" : "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timesheet_${stamp}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E0] rounded-2xl p-6 lg:p-8" data-testid="timesheet-reports-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
          <FileSpreadsheet className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl font-semibold text-[#0F172A]">Employee timesheet reports</h2>
          <p className="text-sm text-[#334155] mt-1 mb-5">
            Download timesheet entries as CSV or PDF — any week, bi-weekly, month, year, or a custom range.
            {isAdmin
              ? " Admin and Supervisor can pull any employee."
              : " As a supervisor you can pull reports for yourself and your direct reports."}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-medium text-[#334155] mb-1 uppercase tracking-wider">Employee</label>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                data-testid="ts-report-user"
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E5E0] text-sm"
              >
                <option value="">Select…</option>
                {employees.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({(u.role || "").replace(/_/g, " ")})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-medium text-[#334155] mb-1 uppercase tracking-wider">Period</label>
              <select
                value={preset}
                onChange={(e) => setPreset(e.target.value)}
                data-testid="ts-report-preset"
                className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E5E0] text-sm"
              >
                <option value="this_week">This week</option>
                <option value="last_week">Last week</option>
                <option value="biweekly">Bi-weekly (last 2 weeks)</option>
                <option value="this_month">This month</option>
                <option value="last_month">Last month</option>
                <option value="this_year">This year</option>
                <option value="last_year">Last year</option>
                <option value="custom">Custom range</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-medium text-[#334155] mb-1 uppercase tracking-wider">From</label>
                <input
                  type="date"
                  value={from}
                  onChange={(e) => { setFrom(e.target.value); setPreset("custom"); }}
                  data-testid="ts-report-from"
                  className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E5E0] text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-[#334155] mb-1 uppercase tracking-wider">To</label>
                <input
                  type="date"
                  value={to}
                  onChange={(e) => { setTo(e.target.value); setPreset("custom"); }}
                  data-testid="ts-report-to"
                  className="w-full px-3 py-2 rounded-xl bg-white border border-[#E5E5E0] text-sm"
                />
              </div>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={() => download("csv")}
              disabled={busy}
              data-testid="ts-report-csv"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-medium hover:opacity-90 disabled:opacity-60"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              onClick={() => download("pdf")}
              disabled={busy}
              data-testid="ts-report-pdf"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#E5E5E0] text-[#1E3A8A] font-medium hover:bg-[#F9F8F6] disabled:opacity-60"
            >
              <FileText className="w-4 h-4" /> PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Render task_description text as a bulleted list. Lines starting with "•" or
 *  "-" already have their own marker → keep as-is; other non-empty lines get
 *  prefixed with a "• " bullet. Single-line plain strings render as a single
 *  bullet so reports look consistent. */
function BulletList({ text }) {
  const value = (text || "").trim();
  if (!value) return null;
  const lines = value.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return null;
  return (
    <ul className="space-y-0.5 text-sm text-[#0F172A]">
      {lines.map((l, i) => {
        const clean = l.replace(/^[•\-*]\s*/, "");
        return (
          <li key={i} className="flex gap-1.5">
            <span className="text-[#1E3A8A]">•</span>
            <span>{clean}</span>
          </li>
        );
      })}
    </ul>
  );
}
